import React, { useState, useEffect } from 'react';
import { deleteOrganizationApi } from '../api/organizations.api';
import { apiHandler } from '../utils/apiHandler';

export default function DeleteOrganizationModal({
  isOpen,
  onClose,
  orgId,
  orgName = 'Organization',
  hasRoster = false,
  onNavigateRoster,
  onDeleted,
}) {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setConfirmText('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isConfirmValid = confirmText.trim() === 'DELETE PERMANENTLY';

  const handleDeleteOrg = async (e) => {
    e.preventDefault();
    if (!isConfirmValid || loading || hasRoster) return;

    setLoading(true);
    setError(null);
    const result = await apiHandler(() => deleteOrganizationApi(orgId));
    if (result.success) {
      if (onDeleted) onDeleted();
      onClose();
    } else {
      setError(result.message || 'Failed to delete organization.');
    }
    setLoading(false);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#FFFFFF',
          borderRadius: 16,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          width: '100%',
          maxWidth: 480,
          padding: '24px 28px',
          animation: 'fadeUp 0.2s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: '#FEF2F2',
                color: '#DC2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
              }}
            >
              <i className="fas fa-triangle-exclamation" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0F172A' }}>
                {hasRoster ? 'Active Member Roster Detected' : 'Delete Organization'}
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748B' }}>
                {orgName}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', background: 'none', fontSize: 20, color: '#94A3B8', cursor: 'pointer', padding: 4 }}
          >
            &times;
          </button>
        </div>

        {error && (
          <div
            style={{
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              color: '#B91C1C',
              padding: '10px 14px',
              borderRadius: 8,
              fontSize: 13,
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <i className="fas fa-circle-exclamation" />
            {error}
          </div>
        )}

        {hasRoster ? (
          <div>
            <div
              style={{
                background: '#FFF7ED',
                border: '1px solid #FFEDD5',
                borderRadius: 10,
                padding: '16px 18px',
                marginBottom: 20,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14, color: '#C2410C', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fas fa-shield-halved" />
                Individual Member Removal Required
              </div>
              <p style={{ margin: 0, fontSize: 13, color: '#9A3412', lineHeight: 1.6 }}>
                This organization currently has active member roster records. In order to protect member accounts and data integrity, you cannot delete this organization while it contains members.
              </p>
              <p style={{ margin: '10px 0 0', fontSize: 13, color: '#9A3412', lineHeight: 1.6, fontWeight: 600 }}>
                Please navigate to the <strong>Roster tab</strong> and remove each member individually before deleting this organization.
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                className="action-btn"
                onClick={onClose}
                style={{ background: '#F1F5F9', border: '1px solid #CBD5E1', color: '#475569', height: 38, padding: '0 18px', borderRadius: 8 }}
              >
                Close
              </button>
              {onNavigateRoster && (
                <button
                  type="button"
                  className="action-btn btn-blue"
                  onClick={() => {
                    onClose();
                    onNavigateRoster();
                  }}
                  style={{
                    height: 38,
                    padding: '0 18px',
                    borderRadius: 8,
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <i className="fas fa-list-check" />
                  Go to Roster Tab
                </button>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleDeleteOrg}>
            <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.5, marginTop: 0, marginBottom: 16 }}>
              This will permanently delete <strong>{orgName}</strong> and remove all associated data. This action <strong>cannot be undone</strong>.
            </p>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
                To confirm, type <span style={{ color: '#DC2626', userSelect: 'all', fontFamily: 'monospace' }}>DELETE PERMANENTLY</span> below:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE PERMANENTLY"
                style={{
                  width: '100%',
                  height: 40,
                  border: isConfirmValid ? '1px solid #22C55E' : '1px solid #CBD5E1',
                  borderRadius: 8,
                  padding: '0 12px',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#0F172A',
                  outline: 'none',
                }}
                disabled={loading}
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                className="action-btn"
                onClick={onClose}
                disabled={loading}
                style={{ background: '#F1F5F9', border: '1px solid #CBD5E1', color: '#475569', height: 38, padding: '0 16px', borderRadius: 8 }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="action-btn"
                disabled={!isConfirmValid || loading}
                style={{
                  background: isConfirmValid ? '#DC2626' : '#F87171',
                  opacity: isConfirmValid ? 1 : 0.6,
                  cursor: isConfirmValid ? 'pointer' : 'not-allowed',
                  color: '#FFFFFF',
                  height: 38,
                  padding: '0 20px',
                  borderRadius: 8,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <i className="fas fa-trash-can" />
                {loading ? 'Deleting...' : 'Delete Organization'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
